import React from 'react';

import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from './utils';


/**
 * Underline tabs matching the ad-hoc tab row in `PartDetailDrawer` (Details /
 * Fitment / Listings / Activity) and the .pen detail panels: a hairline-bottom
 * track with a 2px active underline in primary, active label ink + semibold,
 * idle ink-muted + medium. Built on @radix-ui/react-tabs for roving focus and
 * a11y. The intent is for that ad-hoc row to migrate onto this primitive.
 *
 *   <Tabs defaultValue="details">
 *     <TabsList>
 *       <TabsTrigger value="details">Details</TabsTrigger>
 *       <TabsTrigger value="fitment">Fitment</TabsTrigger>
 *     </TabsList>
 *     <TabsContent value="details">…</TabsContent>
 *   </Tabs>
 */
const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef((props, ref) => {
  const { className, ...restProps } = props;
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn('flex items-center gap-5 border-b border-hairline', className)}
      {...restProps}
    />
  );
});
TabsList.displayName = 'TabsList';

const TabsTrigger = React.forwardRef((props, ref) => {
  const { className, ...restProps } = props;
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'cursor-pointer border-b-2 border-transparent px-[3px] py-3 text-table font-medium text-ink-muted',
        'outline-none transition-colors hover:text-ink',
        'focus-visible:ring-focus focus-visible:rounded-sm',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=active]:border-primary data-[state=active]:font-semibold data-[state=active]:text-ink',
        className,
      )}
      {...restProps}
    />
  );
});
TabsTrigger.displayName = 'TabsTrigger';

const TabsContent = React.forwardRef((props, ref) => {
  const { className, ...restProps } = props;
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn('outline-none focus-visible:ring-focus', className)}
      {...restProps}
    />
  );
});
TabsContent.displayName = 'TabsContent';

export {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
};
export default Tabs;
